import { useCallback, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import type {
  ClientJourneyPresentationV1,
  ExternalProofSalesRoomClientSafeViewV1,
  ExternalAfterFixProofClientSafeSummaryV1,
} from "@enrein/shared-contracts";
import type { ClientPortalShellViewV1 } from "@enrein/shared-contracts/client-link";
import type { DeployEnvironment } from "@enrein/shared-contracts/deploy-env";
import type { PreviewViewV1 } from "@enrein/shared-contracts/approval-change";
import {
  CLIENT_CHANGE_REQUEST_SUBMITTED_MESSAGE,
  CLIENT_INTAKE_SUBMITTED_MESSAGE,
  CLIENT_PREVIEW_APPROVED_MESSAGE,
  CLIENT_PREVIEW_DISCLAIMER,
  toClientApiErrorMessage,
  toClientLinkStatusLabel,
  toSectionStateLabel,
} from "@enrein/shared-contracts/display/tier1-client-display";
import {
  accessDenialFromClientApiError,
  derivePairedAccessIntakeReadiness,
} from "@enrein/shared-contracts";
import { createClientPortalApiClient } from "./api.js";
import { LaunchProofSummaryPanel } from "./LaunchProofSummaryPanel.js";
import { LaunchCertificateSummaryPanel } from "./LaunchCertificateSummaryPanel.js";
import { WebsiteOwnershipChecklistPanel } from "./WebsiteOwnershipChecklistPanel.js";
import { GovernancePanel } from "./GovernancePanel.js";
import { HandoffPanel } from "./HandoffPanel.js";
import { IntakePanel } from "./IntakePanel.js";
import { ReportsPanel } from "./ReportsPanel.js";
import { readClientPortalRuntimeConfig } from "./env.js";
import { clientPortalSessionLocation, resolveClientPortalLink } from "./location.js";
import {
  clearStoredClientLinkSession,
  readStoredClientLinkSession,
  writeStoredClientLinkSession,
} from "./client-link-session-store.js";
import { EnvBanner } from "./EnvBanner.js";
import { ExternalProofSalesRoomPanel } from "./ExternalProofSalesRoomPanel.js";
import { ExternalAfterFixProofPanel } from "./ExternalAfterFixProofPanel.js";
import { ClarificationWorkspace } from "./ClarificationWorkspace.js";
import { InstallPrompt } from "./InstallPrompt.js";
import { canExposePilotSurface, type PilotSurfaceId } from "@enrein/shared-contracts/pilot-exposure";
import { V10ClientLinkApp } from "./features/enrein-v10-client-link/V10ClientLinkApp.js";
import { GreenfieldClientHubApp } from "./features/enrein-greenfield-client-hub/GreenfieldClientHubApp.js";
import { GreenfieldClientBootShell } from "./features/enrein-greenfield-client-hub/chrome/GreenfieldClientShell.js";
import { V10ClientLinkDevFixture } from "./features/enrein-v10-client-link/V10ClientLinkDevFixture.js";
import { V10ClientIntakeDevFixture } from "./features/enrein-v10-client-link/V10ClientIntakeDevFixture.js";
import {
  canMountV10ClientIntakeDevFixture,
  canMountV10ClientLinkDevFixture,
} from "./features/enrein-v10-client-link/routing/v10-dev-fixture-route.js";
import { SafeEditPanel } from "./SafeEditPanel.js";
import lockupUrl from "@enrein/design-system/assets/enrein-lockup-horizontal.svg";

function isClientSurfaceExposed(surfaceId: PilotSurfaceId): boolean {
  return canExposePilotSurface(surfaceId, "client", {
    deployEnv: import.meta.env.VITE_ENREIN_DEPLOY_ENV,
    nodeEnv: import.meta.env.MODE,
    explicitFlags: import.meta.env.VITE_ENREIN_PILOT_FEATURE_FLAGS,
  }).enabled;
}

function GuidedPortalSectionNav() {
  return (
    <nav className="portal-section-nav" aria-label="Client portal sections">
      <a href="#portal-intake">Intake</a>
      <a href="#portal-clarifications">Information requests</a>
      <a href="#portal-preview">Preview</a>
      <a href="#portal-approval">Approval</a>
      <a href="#portal-handoff">Handoff</a>
    </nav>
  );
}

function ShellSection({ section }: { readonly section: ClientPortalShellViewV1["sections"][number] }) {
  return (
    <li className="section-card">
      <div>
        <p className="section-label">{section.label}</p>
        <p className="section-description">{section.description}</p>
      </div>
      <span className={`section-state ${section.state.toLowerCase()}`}>
        {toSectionStateLabel(section.state)}
      </span>
    </li>
  );
}

function PreviewPanel({ preview }: { readonly preview: PreviewViewV1 | null }) {
  if (!preview) return null;
  return (
    <section className="panel preview-chrome" id="portal-preview" aria-label="Website preview">
      <h2>Website preview</h2>
      <div className="preview-chrome-caption">
        <strong>{preview.displayName}</strong>
        <span className="eyebrow" style={{ margin: 0 }}>
          Agency-built snapshot
        </span>
      </div>
      <p>{CLIENT_PREVIEW_DISCLAIMER}</p>
      <div className="preview-frame" dangerouslySetInnerHTML={{ __html: preview.previewHtml }} />
    </section>
  );
}

function SafeShell({
  shell,
  preview,
  changeMessage,
  setChangeMessage,
  onApprove,
  onRequestChange,
  actionStatus,
  extraPanels,
  deployEnv,
  externalProofView = null,
  afterFixProofSummary = null,
  isExternalProof = false,
}: {
  readonly shell: ClientPortalShellViewV1;
  readonly preview: PreviewViewV1 | null;
  readonly changeMessage: string;
  readonly setChangeMessage: (value: string) => void;
  readonly onApprove: () => void;
  readonly onRequestChange: () => void;
  readonly actionStatus: string | null;
  readonly extraPanels?: ReactNode;
  readonly deployEnv: DeployEnvironment;
  readonly externalProofView?: ExternalProofSalesRoomClientSafeViewV1 | null;
  readonly afterFixProofSummary?: ExternalAfterFixProofClientSafeSummaryV1 | null;
  readonly isExternalProof?: boolean;
}) {
  const canAct = ["PREVIEW_READY", "CHANGE_REQUESTED", "APPROVAL_PENDING"].includes(shell.state.status);
  const statusLabel = toClientLinkStatusLabel(shell.state.status);
  const heroHeadline = isExternalProof
    ? shell.headline
    : preview
      ? "Your website preview is ready"
      : shell.headline;
  const heroSubcopy = isExternalProof
    ? shell.subcopy
    : preview
      ? "Review the snapshot below, then approve or send a change request to your agency."
      : shell.subcopy;
  const progressSteps = isExternalProof
    ? [
        { id: "review", label: "Review", active: Boolean(externalProofView) },
        { id: "decide", label: "Decide", active: canAct },
        {
          id: "approve",
          label: "Approved",
          active:
            shell.state.status === "APPROVED" ||
            shell.state.status === "PORTAL_ACTIVE" ||
            externalProofView?.approvalState === "approved",
        },
      ]
    : [
        { id: "preview", label: "Preview", active: Boolean(preview) },
        { id: "review", label: "Review", active: canAct },
        {
          id: "approve",
          label: "Approve",
          active:
            shell.state.status === "APPROVED" ||
            shell.state.status === "PORTAL_ACTIVE" ||
            shell.state.status === "HANDOFF_READY",
        },
      ];
  return (
    <>
      <EnvBanner deployEnv={deployEnv} />
      <a className="enrein-skip-link" href="#client-main">
        Skip to content
      </a>
      <main className="client-shell client-review-room" id="client-main">
        <div className="client-topbar">
          <img src={lockupUrl} alt="Enrein" className="enrein-brand-lockup enrein-brand-lockup--header" />
          <p className="eyebrow client-topbar-label">Client review room</p>
        </div>
        <header className="hero client-journey-hero">
          <div className="client-hero-grid">
            <div>
              <p className="eyebrow">Your project</p>
              <h1>{heroHeadline}</h1>
              <p className="project-name">{shell.project.displayName}</p>
              <p className="journey-subtitle">{heroSubcopy}</p>
            </div>
            <aside className="client-hero-card" aria-label="Current project status">
              <span>Current status</span>
              <strong>{statusLabel}</strong>
              <p>Approve when ready, or request changes for your agency to review.</p>
            </aside>
          </div>
          <ul className="progress-rail enrein-progress-steps" aria-label="Approval progress">
            {progressSteps.map((step) => (
              <li key={step.id} className={step.active ? "active" : undefined}>
                {step.label}
              </li>
            ))}
          </ul>
        </header>
        {!isExternalProof ? <PreviewPanel preview={preview} /> : null}
        {isExternalProof && externalProofView ? (
          <ExternalProofSalesRoomPanel
            view={externalProofView}
            canAct={canAct}
            changeMessage={changeMessage}
            setChangeMessage={setChangeMessage}
            onApprove={onApprove}
            onRequestChange={onRequestChange}
            actionStatus={actionStatus}
          />
        ) : null}
        {isExternalProof && afterFixProofSummary ? (
          <ExternalAfterFixProofPanel summary={afterFixProofSummary} />
        ) : null}
        {!isExternalProof && canAct ? (
          <section
            id="portal-approval"
            className="panel panel--approval value-card"
            aria-label="Approval and change request actions"
          >
            <p className="value-card-eyebrow">Your decision</p>
            <h2 className="value-card-headline">Approval</h2>
            <p>Approve the current preview or send a change request. A change request does not directly edit website truth.</p>
            <div className="action-row">
              <button type="button" className="btn-primary" onClick={onApprove}>
                Approve preview
              </button>
            </div>
            <label className="field-label" htmlFor="change-message">
              Change request
            </label>
            <textarea
              id="change-message"
              value={changeMessage}
              onChange={(event) => setChangeMessage(event.target.value)}
              placeholder="Describe the change you want the agency to review."
            />
            <button
              type="button"
              className="btn-secondary"
              onClick={onRequestChange}
              disabled={changeMessage.trim().length === 0}
            >
              Send change request
            </button>
            {actionStatus ? <p className="action-status">{actionStatus}</p> : null}
          </section>
        ) : null}
        {extraPanels}
        {actionStatus && !canAct && !isExternalProof ? <p className="action-status">{actionStatus}</p> : null}
        <section className="panel workflow-areas-panel" aria-label="Project sections">
          <p className="value-card-eyebrow">Progress</p>
          <h2>What unlocks next</h2>
          <p className="journey-subtitle">Each area unlocks as your agency completes the proof chain.</p>
          <ul className="section-list">
            {shell.sections.map((section) => (
              <ShellSection key={section.id} section={section} />
            ))}
          </ul>
        </section>
      </main>
    </>
  );
}

export function App() {
  const config = useMemo(() => readClientPortalRuntimeConfig(), []);
  if (
    canMountV10ClientIntakeDevFixture({
      isDev: import.meta.env.DEV,
      portalUiVariant: config.portalUiVariant,
      pathname: window.location.pathname,
    })
  ) {
    return (
      <div className="enrein-v10-client-link">
        <V10ClientIntakeDevFixture deployEnv={config.deployEnv} />
      </div>
    );
  }
  if (
    canMountV10ClientLinkDevFixture({
      isDev: import.meta.env.DEV,
      portalUiVariant: config.portalUiVariant,
      pathname: window.location.pathname,
    })
  ) {
    return (
      <div className="enrein-v10-client-link">
        <V10ClientLinkDevFixture deployEnv={config.deployEnv} />
      </div>
    );
  }
  const api = useMemo(() => createClientPortalApiClient(config), [config]);
  const linkResolution = useMemo(
    () => resolveClientPortalLink(window.location.pathname, window.location.search, window.location.hash),
    [],
  );
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const locationContext = useMemo(
    () => clientPortalSessionLocation({ resolution: linkResolution, sessionToken }),
    [linkResolution, sessionToken],
  );
  const [shell, setShell] = useState<ClientPortalShellViewV1 | null>(null);
  const [preview, setPreview] = useState<PreviewViewV1 | null>(null);
  const [journeyPresentation, setJourneyPresentation] = useState<ClientJourneyPresentationV1 | null>(null);
  const [externalProofView, setExternalProofView] = useState<ExternalProofSalesRoomClientSafeViewV1 | null>(null);
  const [afterFixProofSummary, setAfterFixProofSummary] =
    useState<ExternalAfterFixProofClientSafeSummaryV1 | null>(null);
  const [changeMessage, setChangeMessage] = useState("");
  const [actionStatus, setActionStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function loadWithSession(clientLinkId: string, bearerToken: string) {
      const next = await api.getShell(clientLinkId, bearerToken);
      if (cancelled) return;
      setSessionToken(bearerToken);
      setShell(next);
      try {
        const journey = await api.getJourneyPresentation(clientLinkId, bearerToken);
        if (!cancelled) setJourneyPresentation(journey);
      } catch {
        if (!cancelled) setJourneyPresentation(null);
      }
      const isExternalProof =
        next.projectWorkflow === "EXTERNAL_PROOF" && isClientSurfaceExposed("client.externalProofPanels");
      if (isExternalProof) {
        try {
          const salesRoom = await api.getExternalProofSalesRoom(clientLinkId, bearerToken);
          if (!cancelled) setExternalProofView(salesRoom);
        } catch {
          if (!cancelled) setExternalProofView(null);
        }
        try {
          const afterFix = await api.getExternalAfterFixProof(clientLinkId, bearerToken);
          if (!cancelled) setAfterFixProofSummary(afterFix);
        } catch {
          if (!cancelled) setAfterFixProofSummary(null);
        }
        if (!cancelled) setPreview(null);
      } else if (
        [
          "PREVIEW_READY",
          "CHANGE_REQUESTED",
          "APPROVAL_PENDING",
          "APPROVED",
          "PORTAL_ACTIVE",
          "SAFE_EDIT_ACTIVE",
          "HANDOFF_READY",
        ].includes(next.state.status)
      ) {
        try {
          const nextPreview = await api.getPreview(clientLinkId, bearerToken);
          if (!cancelled) setPreview(nextPreview);
        } catch {
          if (!cancelled) setPreview(null);
        }
      }
    }

    async function load() {
      if (linkResolution.kind === "invalid_path") {
        setError("invalid_client_link_url");
        return;
      }

      if (linkResolution.kind === "missing_token") {
        const stored = readStoredClientLinkSession(linkResolution.clientLinkId);
        if (!stored) {
          setError("client_link_missing_token");
          return;
        }
        try {
          await loadWithSession(linkResolution.clientLinkId, stored.sessionToken);
          return;
        } catch (err) {
          clearStoredClientLinkSession(linkResolution.clientLinkId);
          if (!cancelled) {
            setError(err instanceof Error ? err.message : String(err));
          }
          return;
        }
      }

      try {
        const session = await api.createSession(linkResolution.clientLinkId, linkResolution.token);
        writeStoredClientLinkSession({
          clientLinkId: linkResolution.clientLinkId,
          sessionToken: session.sessionToken,
          expiresAt: session.expiresAt,
        });
        if (window.location.hash.includes("token=") || window.location.search.includes("token=")) {
          window.history.replaceState(null, "", `/client/${encodeURIComponent(linkResolution.clientLinkId)}`);
        }
        await loadWithSession(linkResolution.clientLinkId, session.sessionToken);
      } catch (err) {
        if (!cancelled) {
          // Keep raw API error codes so Slice A denial presentation can map them.
          setError(err instanceof Error ? err.message : String(err));
        }
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [api, linkResolution]);

  // Poll journey presentation using server-suggested interval for in-progress outcomes.
  useEffect(() => {
    if (!locationContext || !sessionToken || !journeyPresentation) return;
    const intervalMs = journeyPresentation.suggestedPollIntervalMs;
    if (!Number.isFinite(intervalMs) || intervalMs < 1000) return;
    const outcome = journeyPresentation.outcome;
    const shouldPoll =
      outcome.startsWith("build_") ||
      outcome.includes("in_progress") ||
      outcome.includes("submitted") ||
      outcome.includes("checks_running") ||
      outcome === "quality_gate_passed" ||
      outcome === "preview_awaiting_client_review";
    if (!shouldPoll) return;
    let cancelled = false;
    const timer = window.setInterval(() => {
      void api
        .getJourneyPresentation(locationContext.clientLinkId, sessionToken)
        .then((next) => {
          if (!cancelled) setJourneyPresentation(next);
        })
        .catch(() => {
          /* keep last good presentation */
        });
    }, intervalMs);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [
    api,
    journeyPresentation?.outcome,
    journeyPresentation?.suggestedPollIntervalMs,
    locationContext,
    sessionToken,
  ]);

  const reloadPortalSession = useCallback(async () => {
    if (!locationContext || !sessionToken) return;
    const next = await api.getShell(locationContext.clientLinkId, sessionToken);
    setShell(next);
    try {
      const journey = await api.getJourneyPresentation(locationContext.clientLinkId, sessionToken);
      setJourneyPresentation(journey);
    } catch {
      setJourneyPresentation(null);
    }
    const isExternalProof = next.projectWorkflow === "EXTERNAL_PROOF" && isClientSurfaceExposed("client.externalProofPanels");
    if (isExternalProof) {
      try {
        const salesRoom = await api.getExternalProofSalesRoom(locationContext.clientLinkId, sessionToken);
        setExternalProofView(salesRoom);
      } catch {
        setExternalProofView(null);
      }
      try {
        const afterFix = await api.getExternalAfterFixProof(locationContext.clientLinkId, sessionToken);
        setAfterFixProofSummary(afterFix);
      } catch {
        setAfterFixProofSummary(null);
      }
      setPreview(null);
      return;
    }
    if (
      [
        "PREVIEW_READY",
        "CHANGE_REQUESTED",
        "APPROVAL_PENDING",
        "APPROVED",
        "PORTAL_ACTIVE",
        "SAFE_EDIT_ACTIVE",
        "HANDOFF_READY",
      ].includes(next.state.status)
    ) {
      try {
        const nextPreview = await api.getPreview(locationContext.clientLinkId, sessionToken);
        setPreview(nextPreview);
      } catch {
        setPreview(null);
      }
    }
  }, [api, locationContext, sessionToken]);

  async function handleApprove() {
    if (!locationContext || !sessionToken) return;
    if (shell?.projectWorkflow === "EXTERNAL_PROOF") {
      if (!externalProofView) return;
      try {
        await api.approveExternalProofSalesRoom(
          locationContext.clientLinkId,
          sessionToken,
          externalProofView.salesRoomId,
          externalProofView.viewVersion,
          "Client",
        );
        setActionStatus(CLIENT_PREVIEW_APPROVED_MESSAGE);
        setExternalProofView({ ...externalProofView, approvalState: "approved" });
        await reloadPortalSession();
      } catch (err) {
        setActionStatus(
          err instanceof Error ? toClientApiErrorMessage(err.message) : toClientApiErrorMessage(String(err)),
        );
      }
      return;
    }
    if (!preview) return;
    try {
      await api.approve(
        locationContext.clientLinkId,
        sessionToken,
        preview.previewVersionId,
        "Client",
      );
      setActionStatus(CLIENT_PREVIEW_APPROVED_MESSAGE);
      await reloadPortalSession();
    } catch (err) {
      setActionStatus(
        err instanceof Error ? toClientApiErrorMessage(err.message) : toClientApiErrorMessage(String(err)),
      );
    }
  }

  async function handleRequestChange() {
    if (!locationContext || !sessionToken) return;
    const trimmed = changeMessage.trim();
    if (!trimmed) {
      setActionStatus("Describe the change before sending.");
      return;
    }
    try {
      await api.requestChange(locationContext.clientLinkId, sessionToken, trimmed, "Client");
      setActionStatus(CLIENT_CHANGE_REQUEST_SUBMITTED_MESSAGE);
      setChangeMessage("");
      await reloadPortalSession();
    } catch (err) {
      setActionStatus(
        err instanceof Error ? toClientApiErrorMessage(err.message) : toClientApiErrorMessage(String(err)),
      );
    }
  }

  if (error && !shell) {
    const denial = accessDenialFromClientApiError(error);
    const denialPresentation = denial
      ? derivePairedAccessIntakeReadiness({
          projectId: "client_link_denied",
          accessDenial: denial,
          projectExists: true,
        })?.client
      : null;
    return (
      <>
        <InstallPrompt />
        <EnvBanner deployEnv={config.deployEnv} />
        {config.portalUiVariant === "greenfield" ? (
          <GreenfieldClientBootShell
            denial
            title={denialPresentation?.title ?? "Client link unavailable"}
          >
            <p>{denialPresentation?.reason ?? toClientApiErrorMessage(error)}</p>
            {denialPresentation?.recovery ? <p>{denialPresentation.recovery}</p> : null}
            <p>
              <a href="/" data-testid="client-link-error-home">
                Ask your agency for a valid link
              </a>
            </p>
          </GreenfieldClientBootShell>
        ) : (
        <main className="client-shell">
          <section
            className="panel error-panel client-link-error-panel"
            role="alert"
            tabIndex={0}
            data-testid="client-link-error-panel"
          >
            <h1>{denialPresentation?.title ?? "Client link unavailable"}</h1>
            <p>{denialPresentation?.reason ?? toClientApiErrorMessage(error)}</p>
            {denialPresentation?.recovery ? <p>{denialPresentation.recovery}</p> : null}
            <p>
              <a href="/" data-testid="client-link-error-home">
                Ask your agency for a valid link
              </a>
            </p>
          </section>
        </main>
        )}
      </>
    );
  }

  if (!shell) {
    return (
      <>
        <InstallPrompt />
        <EnvBanner deployEnv={config.deployEnv} />
        {config.portalUiVariant === "greenfield" ? (
          <GreenfieldClientBootShell title="Loading your workspace">
            <p>Please wait while we load your project.</p>
          </GreenfieldClientBootShell>
        ) : (
        <main className="client-shell client-review-room">
          <section className="panel loading-panel enrein-loading-line" aria-busy="true">
            <h1>Loading your review room…</h1>
            <p>Please wait while we load your project preview.</p>
          </section>
        </main>
        )}
      </>
    );
  }

  const showEvidencePanels = ["SAFE_EDIT_ACTIVE", "PORTAL_ACTIVE", "HANDOFF_READY", "APPROVED"].includes(
    shell.state.status,
  );
  const showIntakePanel = ["INTAKE_OPEN", "INTAKE_IN_PROGRESS", "INTAKE_SUBMITTED"].includes(shell.state.status);
  const guidedPortal = config.portalUiVariant === "guided";
  const v10Portal = config.portalUiVariant === "v10";
  const greenfieldPortal = config.portalUiVariant === "greenfield";

  if (greenfieldPortal && locationContext && sessionToken && shell) {
    return (
      <>
        <InstallPrompt
          appDisplayName={shell.branding?.businessDisplayName ?? shell.project.displayName}
        />
        <GreenfieldClientHubApp
          shell={shell}
          preview={preview}
          sessionToken={sessionToken}
          locationContext={locationContext}
          externalProofView={externalProofView}
          afterFixProofSummary={afterFixProofSummary}
          deployEnv={config.deployEnv}
          changeMessage={changeMessage}
          setChangeMessage={setChangeMessage}
          onApprove={() => void handleApprove()}
          onRequestChange={() => void handleRequestChange()}
          actionStatus={actionStatus}
          onActionStatus={(message) => {
            setActionStatus(message);
            if (message === CLIENT_INTAKE_SUBMITTED_MESSAGE) {
              void reloadPortalSession();
            }
          }}
          isExternalProof={shell.projectWorkflow === "EXTERNAL_PROOF" && isClientSurfaceExposed("client.externalProofPanels")}
          api={api}
          journeyPresentation={journeyPresentation}
        />
      </>
    );
  }

  if (v10Portal && locationContext && sessionToken && shell) {
    return (
      <div className="enrein-v10-client-link">
        <InstallPrompt />
        <V10ClientLinkApp
          shell={shell}
          preview={preview}
          sessionToken={sessionToken}
          locationContext={locationContext}
          externalProofView={externalProofView}
          afterFixProofSummary={afterFixProofSummary}
          deployEnv={config.deployEnv}
          changeMessage={changeMessage}
          setChangeMessage={setChangeMessage}
          onApprove={() => void handleApprove()}
          onRequestChange={() => void handleRequestChange()}
          actionStatus={actionStatus}
          onActionStatus={(message) => {
            setActionStatus(message);
            if (message === CLIENT_INTAKE_SUBMITTED_MESSAGE) {
              void reloadPortalSession();
            }
          }}
          isExternalProof={shell.projectWorkflow === "EXTERNAL_PROOF" && isClientSurfaceExposed("client.externalProofPanels")}
          api={api}
          journeyPresentation={journeyPresentation}
        />
      </div>
    );
  }

  const shellContent = (
    <>
      {guidedPortal ? <GuidedPortalSectionNav /> : null}
      <SafeShell
        shell={shell}
        deployEnv={config.deployEnv}
        preview={preview}
        changeMessage={changeMessage}
        setChangeMessage={setChangeMessage}
        onApprove={handleApprove}
        onRequestChange={handleRequestChange}
        actionStatus={actionStatus}
        isExternalProof={shell.projectWorkflow === "EXTERNAL_PROOF" && isClientSurfaceExposed("client.externalProofPanels")}
        externalProofView={externalProofView}
        afterFixProofSummary={afterFixProofSummary}
        extraPanels={
        locationContext && sessionToken ? (
          <>
            {showIntakePanel ? (
              <div id="portal-intake">
                <IntakePanel
                api={api}
                clientLinkId={locationContext.clientLinkId}
                token={sessionToken}
                linkStatus={shell.state.status}
                onStatus={setActionStatus}
              />
              </div>
            ) : null}
            <div id="portal-clarifications">
              <ClarificationWorkspace
                api={{
                  listClarifications: () =>
                    api.listClarifications(locationContext.clientLinkId, sessionToken),
                  respondToClarification: (clarificationRequestId, body) =>
                    api.respondToClarification(
                      locationContext.clientLinkId,
                      sessionToken,
                      clarificationRequestId,
                      body,
                    ),
                }}
              />
            </div>
            {shell.state.status === "HANDOFF_READY" && isClientSurfaceExposed("client.handoff") ? (
              <div id="portal-handoff">
                <HandoffPanel api={api} clientLinkId={locationContext.clientLinkId} token={sessionToken} />
              </div>
            ) : null}
            {showEvidencePanels && shell.projectWorkflow !== "EXTERNAL_PROOF" ? (
              guidedPortal ? (
                <details className="portal-advanced">
                  <summary>Advanced</summary>
                  <div className="client-value-grid client-value-grid--single" aria-label="Project evidence and readiness">
                    <ReportsPanel api={api} clientLinkId={locationContext.clientLinkId} token={sessionToken} />
                    <LaunchProofSummaryPanel
                      api={api}
                      clientLinkId={locationContext.clientLinkId}
                      token={sessionToken}
                    />
                    {isClientSurfaceExposed("client.launchCertificateSummary") ? (
                      <LaunchCertificateSummaryPanel
                        api={api}
                        clientLinkId={locationContext.clientLinkId}
                        token={sessionToken}
                      />
                    ) : null}
                    <WebsiteOwnershipChecklistPanel
                      api={api}
                      clientLinkId={locationContext.clientLinkId}
                      token={sessionToken}
                    />
                    {isClientSurfaceExposed("client.governancePanel") ? (
                      <GovernancePanel api={api} clientLinkId={locationContext.clientLinkId} token={sessionToken} />
                    ) : null}
                  </div>
                </details>
              ) : (
              <div className="client-value-grid client-value-grid--single" aria-label="Project evidence and readiness">
                <ReportsPanel api={api} clientLinkId={locationContext.clientLinkId} token={sessionToken} />
                <LaunchProofSummaryPanel
                  api={api}
                  clientLinkId={locationContext.clientLinkId}
                  token={sessionToken}
                />
                {isClientSurfaceExposed("client.launchCertificateSummary") ? (
                  <LaunchCertificateSummaryPanel
                    api={api}
                    clientLinkId={locationContext.clientLinkId}
                    token={sessionToken}
                  />
                ) : null}
                <WebsiteOwnershipChecklistPanel
                  api={api}
                  clientLinkId={locationContext.clientLinkId}
                  token={sessionToken}
                />
                {isClientSurfaceExposed("client.governancePanel") ? (
                  <GovernancePanel api={api} clientLinkId={locationContext.clientLinkId} token={sessionToken} />
                ) : null}
              </div>
              )
            ) : null}
            {shell.state.status === "SAFE_EDIT_ACTIVE" && isClientSurfaceExposed("client.safePatch") ? (
              <SafeEditPanel
                api={api}
                clientLinkId={locationContext.clientLinkId}
                token={sessionToken}
                onStatus={setActionStatus}
              />
            ) : null}
          </>
        ) : undefined
        }
      />
    </>
  );

  return (
    <div className={guidedPortal ? "enrein-guided-portal" : undefined}>
      <InstallPrompt />
      {shellContent}
    </div>
  );
}
